import React, {
    useEffect, useState, useCallback, useRef,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';
import { isNonMmlAdmin } from '../utils/roles';
import { ReceiptPreviewModal, ReceiptThumbnail, thumbnailAriaLabel } from '../Components/ReceiptPreview';
import { ReceiptDetailsModal, shopStateOf } from '../Components/ReceiptDetails';
import { availableFundingSources, earnLocationLabel, NOT_COMMISSARY_LABEL } from '../utils/loyalty';
import { tenderLabel } from './LoyaltyInsights';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

const STATUS_BADGE = {
    submitted: 'badge-info',
    accepted: 'badge-success',
    rejected: 'badge-danger',
    needs_resubmission: 'badge-warning',
};

const STATUS_LABEL = {
    submitted: 'Submitted',
    accepted: 'Accepted',
    rejected: 'Rejected',
    needs_resubmission: 'Needs a better photo',
};

// How the receipt reached us — member upload, the inbound-email lambda
// (loyalty-center), or an admin uploading on a member's behalf
// (somo-circle1-api#662). Mirrors the `source` enum on loyalty.model.js.
const SOURCE_META = {
    upload: { badge: 'badge-light', label: 'Upload' },
    email: { badge: 'badge-info', label: 'Email' },
    admin_upload: { badge: 'badge-secondary', label: 'Admin Upload' },
};

// Legacy receipts persisted before the field existed read back with no
// `source` at all; the schema default is 'upload', so treat a missing value
// the same way rather than showing a blank cell.
const sourceMeta = (source) => SOURCE_META[source] || SOURCE_META.upload;

const SOURCE_FILTERS = [
    { value: 'all', label: 'All sources' },
    { value: 'upload', label: 'Upload' },
    { value: 'email', label: 'Email' },
    { value: 'admin_upload', label: 'Admin Upload' },
];

// Rows per page — matches the rest of the admin portal (REACT_APP_API_LIMIT /
// User.js). The API caps this at 50 regardless.
const PAGE_SIZE = 10;

// Compact page window around the current page so a queue hundreds of pages
// deep doesn't render hundreds of buttons. First/last are always reachable.
const buildPageWindow = (current, total, span = 2) => {
    const start = Math.max(1, current - span);
    const end = Math.min(total, current + span);
    const pages = [];
    for (let i = start; i <= end; i += 1) pages.push(i);
    return pages;
};

// Ticket #3: OCR runs synchronously at intake, before a receipt document
// even exists — by the time this queue sees a receipt, OCR has already
// either succeeded, failed, or been skipped as unsupported. 'pending' is
// the Mongoose schema default, but genuinely reaching this queue with
// ocrStatus still 'pending' shouldn't happen for a real, in-flight receipt
// given that synchronous-at-create design; the case this UI actually needs
// to handle is legacy receipts submitted before ticket #3 existed, which
// have no ocrStatus field at all (Mongoose's .lean() doesn't backfill
// schema defaults onto documents already persisted without the field —
// they read back as undefined, not 'pending'). Labeled distinctly so it
// doesn't read as "still working on it" for a read that's never coming.
// 'not_commissary': OCR read a total fine, but no receipt line matched
// "COMMISSARY". Since somo-circle1-api#796 that usually means an Exchange (on
// base) or off-base store, both of which earn — not an invalid receipt.
// Non-blocking, same as every other OCR outcome: the receipt still reaches
// this queue with its extracted total, and the admin picks on/off base from
// the image (this check can't see a printed DeCA seal, only text).
// 'conversion_failed' (somo-circle1-api#667): a HEIC/HEIF receipt where the
// server-side JPEG conversion itself failed, so OCR never even ran — kept
// distinct from 'unsupported' (a file type Textract can't read, full stop)
// so an admin can tell "we tried to convert this and it didn't work" apart
// from "this was never going to be OCR'd." Without an explicit entry here
// this falls through to the `none` bucket below and reads as "No OCR data",
// which loses that distinction entirely.
const OCR_STATUS_META = {
    completed: { badge: 'badge-success', label: 'OCR read' },
    not_commissary: { badge: 'badge-warning', label: NOT_COMMISSARY_LABEL },
    failed: { badge: 'badge-danger', label: 'OCR failed' },
    unsupported: { badge: 'badge-secondary', label: 'OCR unsupported' },
    conversion_failed: { badge: 'badge-danger', label: 'Conversion failed' },
    pending: { badge: 'badge-secondary', label: 'OCR pending' },
    none: { badge: 'badge-light', label: 'No OCR data' },
};

// OCR statuses where grossTotal is still a usable prefill — 'not_commissary'
// still extracted a real total, it's just flagged for the admin to double-check
// the store itself before approving.
const OCR_HAS_USABLE_TOTAL = ['completed', 'not_commissary'];

// TL request: a plain Valid/Invalid column. not_commissary used to mean
// invalid, but since somo-circle1-api#796 off-base and Exchange receipts earn
// too (#209), so it's Valid like every other ocrStatus — OCR failures
// included, rather than treating "we couldn't read it" as "it's invalid".
// Where the receipt was bought is a separate question: the OCR column asks
// it (NOT_COMMISSARY_LABEL) and the reviewer answers it on Edit / Review.
// receiptValidity is legacy data: only receipts reviewed while the modal
// toggle existed (7e96fb1 -> 5131331) carry it, and that toggle defaulted to
// 'valid'. The admin no longer sends it, so it's null on anything newer.
const RECEIPT_VALIDITY_META = {
    valid: { badge: 'badge-success', label: 'Valid' },
    invalid: { badge: 'badge-danger', label: 'Invalid' },
};

const deriveReceiptValidity = (r) => (r.receiptValidity === 'invalid' ? 'invalid' : 'valid');

// Same SNAP-vs-total sanity check EditLoyaltyReceipt.js enforces before
// letting an admin submit an approve (decideError: "SNAP-tendered amount
// must be between $0 and the receipt total.") — the /approve endpoint
// re-validates this server-side regardless (loyalty.controller.js,
// snapTenderedCents > grossCents -> 400), so this can't actually book bad
// data, but without it a receipt with an OCR-garbled SNAP read would still
// show as bulk-eligible and only fail once the batch is already running.
const hasValidSnapAmount = (r) => !Number.isFinite(r.snapTenderedTotal)
    || (r.snapTenderedTotal >= 0 && r.snapTenderedTotal <= r.grossTotal);

// A receipt read from a forwarded email's body rather than an attachment
// (somo-circle1-api#823): the loyalty-center Lambda writes the body into a
// PDF with this file name. The member can edit that text before forwarding,
// and a Click2Go order arrives as 'completed', so these always need a
// reviewer: never bulk-approved, and badged so the reviewer also checks the
// member's other receipts for the same order (double earning).
const EMAIL_BODY_RECEIPT_FILENAME = 'email-body.pdf';
const isEmailBodyReceipt = (r) => r.fileName === EMAIL_BODY_RECEIPT_FILENAME;

// Bulk approve skips any per-item amount entry, so it's only offered where
// OCR already extracted a trustworthy total — status must still be
// 'submitted' (anything else 409s on /approve), ocrStatus must be exactly
// 'completed' (not 'not_commissary', which OCR_HAS_USABLE_TOTAL also covers
// but which needs a reviewer to pick on/off base from the image — bulk
// approve books everything on base, so it mustn't wave that through), a flagged possible duplicate always
// goes through individual review same as today, and the SNAP amount (if
// OCR found one) must actually fit within the total.
const isBulkEligible = (r) => r.status === 'submitted'
    && r.ocrStatus === 'completed'
    && Number.isFinite(r.grossTotal) && r.grossTotal > 0
    && !r.possibleDuplicate
    && hasValidSnapAmount(r)
    && !isEmailBodyReceipt(r);

// Same dollars->cents conversion EditLoyaltyReceipt.js uses for its own
// (manually-typed) grossTotalInput/snapTotalInput before POSTing /approve.
const toCents = (dollars) => Math.round(Number(dollars) * 100);

const formatDollars = (amount) => `$${Number(amount).toFixed(2)}`;

// The admin who actioned a receipt — populated by the API's adminList as a
// { name, lastName, email } object (null for a still-submitted receipt, or a
// legacy one actioned before reviewedBy was populated). Falls back to the
// email, then a generic label, so the Actions column never renders a bare
// "Reviewed by".
const reviewerLabel = (reviewedBy) => {
    if (!reviewedBy) return null;
    const name = [reviewedBy.name, reviewedBy.lastName].filter(Boolean).join(' ').trim();
    return name || reviewedBy.email || null;
};

// File names (esp. camera-uploaded receipts) are often long, opaque tokens.
// Keeps the table column readable by showing the first/last few characters
// with an ellipsis in between; the full name is still available via the
// title tooltip and is never altered for the actual preview/click handler.
const truncateMiddle = (str, headLen = 12, tailLen = 8) => {
    if (!str || str.length <= headLen + tailLen + 3) return str;
    return `${str.slice(0, headLen)}...${str.slice(-tailLen)}`;
};

// Ticket #4 (duplicate detection): duplicateOfReceiptId is populated by the
// admin GETs with the flagged original's fileUrl/fileName/userId/createdAt/
// status — a plain object here (not a string ID) when present, null/absent
// otherwise. A helper since both the table row and the modal need the same
// "who submitted the original, and when" line.
const duplicateOriginLabel = (original) => {
    if (!original) return null;
    const name = [original.userId?.name, original.userId?.lastName].filter(Boolean).join(' ') || 'another member';
    const when = original.createdAt ? new Date(original.createdAt).toLocaleDateString() : '';
    return `submitted by ${name}${when ? ` on ${when}` : ''}`;
};

function LoyaltyReceipts() {
    const location = useLocation();
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState('submitted');
    const [source, setSource] = useState('all');
    const [nameQuery, setNameQuery] = useState('');
    const [emailQuery, setEmailQuery] = useState('');
    // Extra API filters (from/to/commissaryBaseId/state/userId/product) when
    // the queue was opened from Receipt Insights (somo-circle1-api-admin#188),
    // with the label its banner shows. Kept until cleared or reset.
    const [scope, setScope] = useState({});
    const [scopeLabel, setScopeLabel] = useState('');
    // The Receipt Insights filters to go back to, when the queue was opened
    // from there. Rides along to Edit and back on the queue's own state, so
    // "Back to Receipt Insights" survives reviewing a receipt.
    const [insightsReturn] = useState(() => location.state?.restoreFilters?.insights || null);
    // The same for the Member page (somo-circle1-api-admin#195):
    // { userId, name, filters }.
    const [memberReturn] = useState(() => location.state?.restoreFilters?.memberReturn || null);
    // And for Product Insights (somo-circle1-api-admin#194): its filters,
    // products included.
    const [productReturn] = useState(() => location.state?.restoreFilters?.productReturn || null);
    const [previewImage, setPreviewImage] = useState(null); // { url, label } — in-app preview (image or PDF), not a new tab
    // Receipts emailed from an address with no account, held for triage
    // (somo-circle1-api#691). adminList returns the count with every queue
    // page; the banner linking to them is CircleOne-only, same as the
    // requireNonMmlAdmin gate on the unclaimed-receipts endpoints.
    const [unclaimedCount, setUnclaimedCount] = useState(null);
    const [canTriageUnclaimed, setCanTriageUnclaimed] = useState(false);
    // Stored OCR details for one receipt (somo-circle1-api#777). Same
    // CircleOne-only rule as canTriageUnclaimed: the line-items endpoint is
    // gated by requireNonMmlAdmin.
    const [detailsReceipt, setDetailsReceipt] = useState(null);

    // Bulk approve — selection is scoped to whatever's currently on screen,
    // cleared on every fetchReceipts (status/source/search change, page
    // change, or the post-batch refresh) rather than persisted across pages.
    const selectAllRef = useRef(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [enabledFundingSources, setEnabledFundingSources] = useState(null);
    const [bulkFundingSource, setBulkFundingSource] = useState('');
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [bulkInProgress, setBulkInProgress] = useState(false);
    const [bulkProgress, setBulkProgress] = useState(0);
    const [bulkResults, setBulkResults] = useState(null); // [{ id, receipt, ok, clamped, message }] once the batch finishes
    const fundingSourceOptions = availableFundingSources(enabledFundingSources);

    const openPreview = (url, label) => setPreviewImage({ url, label });
    const closePreview = () => setPreviewImage(null);


    // Best-effort funding-source list for the bulk-approve toolbar — same
    // GET /admin/loyalty/config read EditLoyaltyReceipt.js uses. A fetch
    // failure just falls back to the full canonical set
    // (availableFundingSources(null)).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await authManager.authenticatedFetch(`${Base_Url}/admin/loyalty/config`);
                const data = await res.json();
                if (!cancelled && res.ok && Array.isArray(data.data?.enabledFundingSources)) {
                    setEnabledFundingSources(data.data.enabledFundingSources);
                }
            } catch (err) {
                // Falls back to availableFundingSources(null) — the full set.
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Same rule as Sidebar.js's showSettings. A failed lookup hides the
    // banner rather than linking an MML admin to a screen that 403s.
    useEffect(() => {
        let cancelled = false;
        authManager.validateUser()
            .then((user) => {
                if (!cancelled) {
                    setCanTriageUnclaimed(isNonMmlAdmin(user));
                }
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // Default the shared bulk-approve funding source to the first offered
    // option once the config loads (or reconcile it if the enabled set
    // changes while the current pick is no longer offered) — same pattern
    // EditLoyaltyReceipt.js uses for its own funding-source select. Also
    // explicitly clears it when the enabled set becomes empty — without
    // this branch, a value picked while options were still the pre-config-
    // load full default set (availableFundingSources(null)) would survive
    // the config later loading with everything disabled, since the "pick a
    // default" branch below only ever runs when there's something to pick.
    useEffect(() => {
        if (fundingSourceOptions.length === 0) {
            if (bulkFundingSource) setBulkFundingSource('');
        } else if (!bulkFundingSource || !fundingSourceOptions.some((f) => f.value === bulkFundingSource)) {
            setBulkFundingSource(fundingSourceOptions[0].value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabledFundingSources]);

    // Snapshot of the filter set last sent to the server (see fetchReceipts).
    // The status dropdown, pagination and post-action refresh page against this
    // rather than the live input state, so an un-submitted name/email search
    // never applies on the next status change or page click.
    const appliedFiltersRef = useRef({
        status: 'submitted', source: 'all', name: '', email: '', scope: {}, scopeLabel: '',
    });

    // Filters are passed in explicitly (not read from state) so this stays a
    // stable, dependency-free callback — every caller hands it the values it
    // wants applied, same reasoning as the eslint-disable below.
    const fetchReceipts = useCallback(async (targetPage = 1, filters = {}) => {
        const {
            status: fStatus = 'submitted',
            source: fSource = 'all',
            name: fName = '',
            email: fEmail = '',
            scope: fScope = {},
            scopeLabel: fScopeLabel = '',
        } = filters;
        // Snapshot of the filter set actually sent to the server. The status
        // dropdown, pagination buttons and post-action refresh page against
        // THIS — not the live input state — so typing (but not submitting) a
        // name/email search doesn't silently apply on the next status change
        // or page click.
        appliedFiltersRef.current = {
            status: fStatus, source: fSource, name: fName, email: fEmail, scope: fScope, scopeLabel: fScopeLabel,
        };
        setScope(fScope);
        setScopeLabel(fScopeLabel);
        // Selection is scoped to what's currently on screen — any refetch
        // (filter change, page change, post-bulk refresh) invalidates it.
        setSelectedIds(new Set());
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page: targetPage,
                limit: PAGE_SIZE,
                status: fStatus,
            });
            if (fSource && fSource !== 'all') queryParams.set('source', fSource);
            if (fName.trim()) queryParams.set('name', fName.trim());
            if (fEmail.trim()) queryParams.set('email', fEmail.trim());
            Object.entries(fScope).forEach(([key, value]) => {
                if (value) queryParams.set(key, value);
            });
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/loyalty/receipts?${queryParams}`,
            );
            const data = await res.json();
            if (data.status === 'success') {
                setReceipts(data.data.receipts || []);
                setPagination(data.data.pagination || {});
                setUnclaimedCount(Number.isFinite(data.data.unclaimedCount) ? data.data.unclaimedCount : null);
                setPage(targetPage);
            } else {
                toast.error(data.message || 'Failed to load the review queue.');
            }
        } catch (err) {
            console.error('Error fetching loyalty receipts:', err);
            toast.error('Error loading the review queue.');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Live filter state straight off the inputs — only the Search button acts
    // on this. Everything else pages against appliedFiltersRef (see fetchReceipts).
    const liveFilters = {
        status, source, name: nameQuery, email: emailQuery, scope, scopeLabel,
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // Coming back from Edit/Review (Approve/Reject, or "Back to Receipts")
        // carries the page/filters the admin was on before they clicked into
        // that receipt — restore them instead of always resetting to page 1.
        const restore = location.state?.restoreFilters;
        if (restore) {
            setStatus(restore.status || 'submitted');
            setSource(restore.source || 'all');
            setNameQuery(restore.name || '');
            setEmailQuery(restore.email || '');
            fetchReceipts(restore.page || 1, {
                status: restore.status || 'submitted',
                source: restore.source || 'all',
                name: restore.name || '',
                email: restore.email || '',
                scope: restore.scope || {},
                scopeLabel: restore.scopeLabel || '',
            });
        } else {
            fetchReceipts(1, { status: 'submitted' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleStatusChange = (e) => {
        const next = e.target.value;
        setStatus(next);
        // Carry the last *applied* search, not whatever is currently typed.
        fetchReceipts(1, { ...appliedFiltersRef.current, status: next });
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        fetchReceipts(1, liveFilters);
    };

    const handleResetFilters = () => {
        setStatus('submitted');
        setSource('all');
        setNameQuery('');
        setEmailQuery('');
        fetchReceipts(1, { status: 'submitted' });
    };

    const handleClearScope = () => {
        fetchReceipts(1, { ...appliedFiltersRef.current, scope: {}, scopeLabel: '' });
    };

    const eligibleOnPage = receipts.filter(isBulkEligible);
    const selectedReceipts = receipts.filter((r) => selectedIds.has(r._id));
    const selectedTotal = selectedReceipts.reduce((sum, r) => sum + (Number(r.grossTotal) || 0), 0);

    // Native checkboxes have no "indeterminate" prop — it's a DOM-only
    // property, so it's imperatively set on the underlying element here
    // rather than passed as JSX.
    useEffect(() => {
        if (selectAllRef.current) {
            const selectedEligibleCount = eligibleOnPage.filter((r) => selectedIds.has(r._id)).length;
            selectAllRef.current.indeterminate = selectedEligibleCount > 0 && selectedEligibleCount < eligibleOnPage.length;
        }
    });

    const toggleSelectAll = () => {
        const allSelected = eligibleOnPage.length > 0 && eligibleOnPage.every((r) => selectedIds.has(r._id));
        setSelectedIds(allSelected ? new Set() : new Set(eligibleOnPage.map((r) => r._id)));
    };

    const toggleSelectOne = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    // Sequential, not parallel — each /approve call does its own atomic
    // status-guarded claim and (for our_pl) reads/writes the same monthly
    // cap, so racing several at once within one batch would let them step
    // on each other's cap read. No new backend endpoint: this just calls
    // the existing single-receipt endpoint once per selected receipt.
    const handleBulkApprove = async () => {
        setBulkInProgress(true);
        setBulkProgress(0);
        const results = [];
        for (let i = 0; i < selectedReceipts.length; i += 1) {
            const r = selectedReceipts[i];
            try {
                // eslint-disable-next-line no-await-in-loop
                const res = await authManager.authenticatedFetch(
                    `${Base_Url}/admin/loyalty/receipts/${r._id}/approve`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            grossCents: toCents(r.grossTotal),
                            snapTenderedCents: toCents(r.snapTenderedTotal || 0),
                            fundingSource: bulkFundingSource,
                            // isBulkEligible only lets through receipts OCR
                            // matched to a Commissary, so these are on base
                            // (somo-circle1-api#796).
                            earnLocation: 'on_base',
                        }),
                    },
                );
                // eslint-disable-next-line no-await-in-loop
                const data = await res.json();
                if (data.status !== 'success') {
                    results.push({
                        id: r._id, receipt: r, ok: false, clamped: false, message: data.message || 'That action failed.',
                    });
                } else {
                    // clamped: true means /approve still returned 200 and flipped the
                    // receipt to accepted, but the member's our_pl monthly cap was
                    // already reached, so fewer (possibly zero) points were actually
                    // booked — a real outcome to call out, not a plain success.
                    results.push({
                        id: r._id,
                        receipt: r,
                        ok: true,
                        clamped: Boolean(data.data?.clamped),
                        message: data.message || 'Receipt approved.',
                    });
                }
            } catch (err) {
                results.push({
                    id: r._id, receipt: r, ok: false, clamped: false, message: err.message || 'That action failed.',
                });
            }
            setBulkProgress(i + 1);
        }
        setBulkResults(results);
        setBulkInProgress(false);
        const succeeded = results.filter((r) => r.ok && !r.clamped).length;
        const clamped = results.filter((r) => r.ok && r.clamped).length;
        const failed = results.filter((r) => !r.ok).length;
        if (failed === 0 && clamped === 0) {
            toast.success(`Approved ${succeeded} receipt${succeeded === 1 ? '' : 's'}.`);
        } else {
            toast.warning(`${succeeded} approved, ${clamped} capped at reduced points, ${failed} failed — see the results below.`);
        }
    };

    const closeBulkModal = () => {
        const hadResults = !!bulkResults;
        setBulkModalOpen(false);
        setBulkResults(null);
        setBulkProgress(0);
        if (hadResults) {
            // Refetches page 1 rather than the current page — adminList doesn't
            // clamp an out-of-range page against the post-approval item count, so
            // re-requesting the same page after clearing out everything on it
            // (e.g. the last page of the queue) would land on an empty page with
            // no page button showing as "current". fetchReceipts also clears
            // selectedIds as part of any refetch, so no separate clear is needed
            // here — Cancel (no results) deliberately leaves the selection intact.
            fetchReceipts(1, appliedFiltersRef.current);
        }
    };

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">Loyalty Receipts</h1>
                        </div>
                        <div className="col-sm-6 txt_rgt">
                            {insightsReturn && (
                                <Link
                                    to="/loyaltyInsights"
                                    state={{ restoreFilters: insightsReturn }}
                                    className="btn btn-outline-secondary mr-2"
                                >
                                    <i className="fa-solid fa-arrow-left-long" /> Back to Receipt Insights
                                </Link>
                            )}
                            {memberReturn && (
                                <Link
                                    to={`/loyaltyMembers/${memberReturn.userId}`}
                                    state={{ restoreFilters: memberReturn.filters }}
                                    className="btn btn-outline-secondary mr-2"
                                >
                                    <i className="fa-solid fa-arrow-left-long" /> Back to {memberReturn.name}
                                </Link>
                            )}
                            {productReturn && (
                                <Link
                                    to="/loyaltyProductInsights"
                                    state={{ restoreFilters: productReturn }}
                                    className="btn btn-outline-secondary mr-2"
                                >
                                    <i className="fa-solid fa-arrow-left-long" /> Back to Product Insights
                                </Link>
                            )}
                            <Link to="/loyaltyReceipts/upload" className="btn btn-blue">
                                <i className="fa-solid fa-plus" /> Upload Receipt
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    {canTriageUnclaimed && unclaimedCount > 0 && (
                        <div
                            className="alert alert-warning d-flex flex-wrap align-items-center justify-content-between"
                            role="status"
                            data-testid="unclaimed-banner"
                        >
                            <span>
                                <i className="fa-solid fa-envelope-open-text mr-2" aria-hidden="true" />
                                <strong>{unclaimedCount}</strong> receipt{unclaimedCount === 1 ? '' : 's'} emailed
                                {' '}from {unclaimedCount === 1 ? 'an address' : 'addresses'} with no account {unclaimedCount === 1 ? 'is' : 'are'} waiting to be claimed.
                            </span>
                            <Link to="/loyaltyReceipts/unclaimed" className="btn btn-sm btn-outline-dark">
                                Review unclaimed receipts
                            </Link>
                        </div>
                    )}
                    {Object.keys(scope).length > 0 && (
                        <div
                            className="alert alert-info d-flex flex-wrap align-items-center justify-content-between"
                            style={{ gap: '8px' }}
                            data-testid="insights-scope-banner"
                        >
                            <span>
                                Showing receipts from{' '}
                                {memberReturn && (
                                    <Link to={`/loyaltyMembers/${memberReturn.userId}`} state={{ restoreFilters: memberReturn.filters }}>
                                        {memberReturn.name}&apos;s member page
                                    </Link>
                                )}
                                {!memberReturn && productReturn && (
                                    <Link to="/loyaltyProductInsights" state={{ restoreFilters: productReturn }}>
                                        Product Insights
                                    </Link>
                                )}
                                {!memberReturn && !productReturn && (
                                    <Link to="/loyaltyInsights" state={insightsReturn ? { restoreFilters: insightsReturn } : undefined}>
                                        Receipt Insights
                                    </Link>
                                )}
                                {scopeLabel ? `: ${scopeLabel}` : ''}
                                {/* Named from the scope, whether the page's filter or the row clicked set it. */}
                                {scope.tender ? `${scopeLabel ? ' ·' : ':'} Paid with ${tenderLabel(scope.tender)}` : ''}
                                {scope.from || scope.to ? ` (${scope.from || '…'} to ${scope.to || '…'})` : ''}
                            </span>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleClearScope}>
                                Clear
                            </button>
                        </div>
                    )}
                    <div className="card mb-3">
                        <form className="card-body" onSubmit={handleSearchSubmit}>
                            <div className="row align-items-end">
                                <div className="col-md-2">
                                    <label htmlFor="receipt-status-filter">Status</label>
                                    <select
                                        id="receipt-status-filter"
                                        className="form-control"
                                        value={status}
                                        onChange={handleStatusChange}
                                    >
                                        <option value="submitted">Submitted (queue)</option>
                                        <option value="accepted">Accepted</option>
                                        <option value="rejected">Rejected</option>
                                        <option value="needs_resubmission">Needs a better photo</option>
                                        <option value="all">All</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label htmlFor="receipt-source-filter">Source</label>
                                    <select
                                        id="receipt-source-filter"
                                        className="form-control"
                                        value={source}
                                        onChange={(e) => setSource(e.target.value)}
                                    >
                                        {SOURCE_FILTERS.map((s) => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label htmlFor="receipt-name-filter">Member name</label>
                                    <input
                                        id="receipt-name-filter"
                                        type="text"
                                        className="form-control"
                                        value={nameQuery}
                                        onChange={(e) => setNameQuery(e.target.value)}
                                        placeholder="Search by name"
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label htmlFor="receipt-email-filter">Member email</label>
                                    <input
                                        id="receipt-email-filter"
                                        type="text"
                                        className="form-control"
                                        value={emailQuery}
                                        onChange={(e) => setEmailQuery(e.target.value)}
                                        placeholder="Search by email"
                                    />
                                </div>
                                <div className="col-md-2 d-flex" style={{ gap: '8px' }}>
                                    <button
                                        type="submit"
                                        className="btn btn-primary flex-fill"
                                        style={{ backgroundColor: 'var(--primary)', borderColor: 'var(--primary)' }}
                                    >
                                        Search
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary flex-fill"
                                        onClick={handleResetFilters}
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="row">
                        <div className="col-lg-12">
                            {selectedIds.size > 0 && (
                                <div
                                    className="d-flex flex-wrap align-items-center justify-content-between bg-light border rounded p-2 mb-2"
                                    style={{ gap: '8px' }}
                                >
                                    <div data-testid="bulk-selection-summary">
                                        <strong>{selectedIds.size}</strong> receipt{selectedIds.size === 1 ? '' : 's'} selected
                                        {' '}— total {formatDollars(selectedTotal)}
                                    </div>
                                    <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                                        <label htmlFor="bulk-funding-source" className="mb-0 mr-1">Rewards Funding Source</label>
                                        <select
                                            id="bulk-funding-source"
                                            className="form-control form-control-sm"
                                            style={{ width: 'auto' }}
                                            value={bulkFundingSource}
                                            onChange={(e) => setBulkFundingSource(e.target.value)}
                                        >
                                            {fundingSourceOptions.map((f) => (
                                                <option key={f.value} value={f.value}>{f.label}</option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary"
                                            style={{ backgroundColor: 'var(--primary)', borderColor: 'var(--primary)' }}
                                            disabled={!bulkFundingSource || fundingSourceOptions.length === 0}
                                            onClick={() => setBulkModalOpen(true)}
                                        >
                                            Approve Selected
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => setSelectedIds(new Set())}
                                        >
                                            Clear
                                        </button>
                                        {fundingSourceOptions.length === 0 && (
                                            <small className="text-danger d-block w-100 mb-0">
                                                No funding sources are currently enabled — enable at least one on Loyalty Program Rules before approving.
                                            </small>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center p-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                ) : receipts.length > 0 ? (
                                    <>
                                        <table width="100%" className="table table-striped table-bordered txt_lft">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '32px' }}>
                                                        <input
                                                            type="checkbox"
                                                            ref={selectAllRef}
                                                            aria-label="Select all bulk-approve-eligible receipts on this page"
                                                            checked={eligibleOnPage.length > 0 && eligibleOnPage.every((r) => selectedIds.has(r._id))}
                                                            disabled={eligibleOnPage.length === 0}
                                                            onChange={toggleSelectAll}
                                                        />
                                                    </th>
                                                    <th style={{ width: '70px' }}>Receipt</th>
                                                    <th style={{ width: '140px' }}>Member</th>
                                                    <th style={{ width: '180px' }}>File</th>
                                                    <th style={{ width: '80px' }}>Source</th>
                                                    <th style={{ width: '130px' }}>OCR</th>
                                                    <th style={{ width: '100px' }}>Validity</th>
                                                    <th style={{ width: '140px' }}>Submitted</th>
                                                    <th style={{ width: '110px' }}>Status</th>
                                                    <th style={{ width: '220px' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {receipts.map((r) => (
                                                    <tr key={r._id}>
                                                        <td>
                                                            {isBulkEligible(r) ? (
                                                                <input
                                                                    type="checkbox"
                                                                    aria-label={`Select receipt from ${r.userId?.name || 'this member'} for bulk approve`}
                                                                    checked={selectedIds.has(r._id)}
                                                                    onChange={() => toggleSelectOne(r._id)}
                                                                />
                                                            ) : (
                                                                <span
                                                                    className="text-muted fast-tooltip"
                                                                    data-tooltip="Not eligible for bulk approve — needs a submitted receipt with an OCR-read amount and no duplicate flag"
                                                                    title="Not eligible for bulk approve — needs a submitted receipt with an OCR-read amount and no duplicate flag"
                                                                >
                                                                    —
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn p-0 border-0 bg-transparent"
                                                                aria-label={thumbnailAriaLabel(r.fileUrl)}
                                                                onClick={() => openPreview(r.fileUrl, r.fileName || 'Receipt')}
                                                            >
                                                                <ReceiptThumbnail fileUrl={r.fileUrl} />
                                                            </button>
                                                        </td>
                                                        <td>
                                                            <span
                                                                className={`fwt_600${r.userId?.email ? ' fast-tooltip' : ''}`}
                                                                data-tooltip={r.userId?.email}
                                                                title={r.userId?.email || undefined}
                                                                aria-label={r.userId?.email ? `${r.userId?.name} ${r.userId?.lastName}, ${r.userId.email}` : undefined}
                                                                tabIndex={r.userId?.email ? 0 : undefined}
                                                            >
                                                                {r.userId?.name} {r.userId?.lastName}
                                                            </span>
                                                            {/* The Member page (#195) is CircleOne-only. */}
                                                            {canTriageUnclaimed && r.userId?._id && (
                                                                <Link
                                                                    to={`/loyaltyMembers/${r.userId._id}`}
                                                                    className="ml-1 text-muted"
                                                                    title="Member receipts and products"
                                                                    aria-label={`Open the member page for ${r.userId?.name || 'this member'}`}
                                                                >
                                                                    <i className="fa-solid fa-chart-column" aria-hidden="true" />
                                                                </Link>
                                                            )}
                                                            {/* somo-circle1-api#777 — a hint the member may
                                                                have moved; never affects approval.
                                                                CircleOne-only, like the Details panel. */}
                                                            {canTriageUnclaimed && r.locationMismatch === true && (
                                                                <>
                                                                    <br />
                                                                    <small
                                                                        className="text-warning fast-tooltip"
                                                                        data-tooltip={`Shopped in ${shopStateOf(r)}, profile says ${r.profileState || '?'} — informational only`}
                                                                        title={`Shopped in ${shopStateOf(r)}, profile says ${r.profileState || '?'} — informational only`}
                                                                    >
                                                                        Out-of-state shop ({shopStateOf(r)})
                                                                    </small>
                                                                </>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-link p-0 border-0 align-baseline f_sz13 fast-tooltip"
                                                                data-tooltip={r.fileName || 'Receipt'}
                                                                title={r.fileName || 'Receipt'}
                                                                onClick={() => openPreview(r.fileUrl, r.fileName || 'Receipt')}
                                                            >
                                                                {truncateMiddle(r.fileName) || 'Receipt'}
                                                            </button>
                                                            {r.rejectionReason && (
                                                                <>
                                                                    <br />
                                                                    <small className="text-muted">Note: {r.rejectionReason}</small>
                                                                </>
                                                            )}
                                                            {r.possibleDuplicate && (
                                                                <>
                                                                    <br />
                                                                    <small className="text-danger">
                                                                        ⚠ Possible duplicate
                                                                        {r.duplicateOfReceiptId && (
                                                                            <>
                                                                                {' '}of{' '}
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-link p-0 border-0 align-baseline text-danger"
                                                                                    onClick={() => openPreview(
                                                                                        r.duplicateOfReceiptId.fileUrl,
                                                                                        r.duplicateOfReceiptId.fileName || 'Original receipt',
                                                                                    )}
                                                                                >
                                                                                    {r.duplicateOfReceiptId.fileName || 'this receipt'}
                                                                                </button>
                                                                                {' '}({duplicateOriginLabel(r.duplicateOfReceiptId)})
                                                                            </>
                                                                        )}
                                                                    </small>
                                                                </>
                                                            )}
                                                            {!r.possibleDuplicate && r.dedupeChecked === false && OCR_HAS_USABLE_TOTAL.includes(r.ocrStatus) && (
                                                                <>
                                                                    <br />
                                                                    <small className="text-warning">
                                                                        Dedupe not checked (not enough on the receipt to fingerprint it)
                                                                    </small>
                                                                </>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${sourceMeta(r.source).badge}`}>
                                                                {sourceMeta(r.source).label}
                                                            </span>
                                                            {isEmailBodyReceipt(r) && (
                                                                <>
                                                                    <br />
                                                                    <span className="badge badge-warning">From email body</span>
                                                                </>
                                                            )}
                                                            {/* Emailed before the sender had an account, then
                                                                claimed (somo-circle1-api#691). */}
                                                            {r.unclaimedReceiptId && (
                                                                <>
                                                                    <br />
                                                                    <small className="text-muted">Held before sign-up</small>
                                                                </>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {OCR_HAS_USABLE_TOTAL.includes(r.ocrStatus) && Number.isFinite(r.grossTotal) ? (
                                                                <>
                                                                    <span
                                                                        className={`badge ${r.ocrStatus === 'not_commissary' ? 'badge-warning' : 'badge-success'}`}
                                                                    >
                                                                        {formatDollars(r.grossTotal)}
                                                                    </span>
                                                                    {r.ocrStatus === 'not_commissary' && (
                                                                        <>
                                                                            <br />
                                                                            <small className="text-warning">{NOT_COMMISSARY_LABEL}</small>
                                                                        </>
                                                                    )}
                                                                    {r.paymentMethodLine && (
                                                                        <>
                                                                            <br />
                                                                            <small className="text-muted">{r.paymentMethodLine}</small>
                                                                        </>
                                                                    )}
                                                                    {Number.isFinite(r.snapTenderedTotal) && (
                                                                        <>
                                                                            <br />
                                                                            <small className="text-muted">
                                                                                SNAP: {formatDollars(r.snapTenderedTotal)}
                                                                            </small>
                                                                        </>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <span className={`badge ${(OCR_STATUS_META[r.ocrStatus] || OCR_STATUS_META.none).badge}`}>
                                                                    {(OCR_STATUS_META[r.ocrStatus] || OCR_STATUS_META.none).label}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${RECEIPT_VALIDITY_META[deriveReceiptValidity(r)].badge}`}>
                                                                {RECEIPT_VALIDITY_META[deriveReceiptValidity(r)].label}
                                                            </span>
                                                        </td>
                                                        <td className='f_sz13' >{new Date(r.createdAt).toLocaleString()}</td>
                                                        <td>
                                                            <span className={`badge ${STATUS_BADGE[r.status] || 'badge-secondary'}`}>
                                                                {STATUS_LABEL[r.status] || r.status}
                                                            </span>
                                                            {r.status === 'accepted' && r.earnLocation && (
                                                                <small className="d-block text-muted mt-1" data-testid="earn-location">
                                                                    {earnLocationLabel(r.earnLocation, r.earnPercentageApplied)}
                                                                </small>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {['submitted', 'needs_resubmission'].includes(r.status) ? (
                                                                <Link
                                                                    to={`/loyaltyReceipts/${r._id}/edit`}
                                                                    state={{
                                                                        from: {
                                                                            page, ...appliedFiltersRef.current, insights: insightsReturn, memberReturn, productReturn,
                                                                        },
                                                                    }}
                                                                    className="btn btn-sm btn-primary"
                                                                    // customstyle.css forces .btn-primary's resting
                                                                    // background to black app-wide (only turns blue
                                                                    // on hover) — overridden here to this app's own
                                                                    // --primary navy-blue token instead of a raw hex,
                                                                    // so it stays in sync if the theme's blue changes.
                                                                    style={{ backgroundColor: 'var(--primary)', borderColor: 'var(--primary)' }}
                                                                >
                                                                    Edit / Review
                                                                </Link>
                                                            ) : (
                                                                <small className="text-muted">
                                                                    Reviewed
                                                                    {reviewerLabel(r.reviewedBy) ? ` by ${reviewerLabel(r.reviewedBy)}` : ''}
                                                                    {r.reviewedAt ? ` on ${new Date(r.reviewedAt).toLocaleString()}` : ''}
                                                                </small>
                                                            )}
                                                            {canTriageUnclaimed && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-secondary ml-1 mt-1"
                                                                    aria-label={`View stored details for the receipt from ${r.userId?.name || 'this member'}`}
                                                                    onClick={() => setDetailsReceipt(r)}
                                                                >
                                                                    Details
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {pagination.totalPages > 1 && (() => {
                                            const pages = buildPageWindow(page, pagination.totalPages);
                                            return (
                                                <nav className="mt-3 d-flex flex-column align-items-center">
                                                    <ul className="pagination justify-content-center mb-1">
                                                        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => fetchReceipts(page - 1, appliedFiltersRef.current)}
                                                                disabled={page === 1}
                                                            >
                                                                Previous
                                                            </button>
                                                        </li>
                                                        {pages[0] > 1 && (
                                                            <>
                                                                <li className="page-item">
                                                                    <button className="page-link" onClick={() => fetchReceipts(1, appliedFiltersRef.current)}>1</button>
                                                                </li>
                                                                {pages[0] > 2 && (
                                                                    <li className="page-item disabled"><span className="page-link">…</span></li>
                                                                )}
                                                            </>
                                                        )}
                                                        {pages.map((p) => (
                                                            <li key={p} className={`page-item ${page === p ? 'active' : ''}`}>
                                                                <button className="page-link" onClick={() => fetchReceipts(p, appliedFiltersRef.current)}>
                                                                    {p}
                                                                </button>
                                                            </li>
                                                        ))}
                                                        {pages[pages.length - 1] < pagination.totalPages && (
                                                            <>
                                                                {pages[pages.length - 1] < pagination.totalPages - 1 && (
                                                                    <li className="page-item disabled"><span className="page-link">…</span></li>
                                                                )}
                                                                <li className="page-item">
                                                                    <button
                                                                        className="page-link"
                                                                        onClick={() => fetchReceipts(pagination.totalPages, appliedFiltersRef.current)}
                                                                    >
                                                                        {pagination.totalPages}
                                                                    </button>
                                                                </li>
                                                            </>
                                                        )}
                                                        <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => fetchReceipts(page + 1, appliedFiltersRef.current)}
                                                                disabled={page === pagination.totalPages}
                                                            >
                                                                Next
                                                            </button>
                                                        </li>
                                                    </ul>
                                                    <small className="text-muted">
                                                        Page {pagination.currentPage} of {pagination.totalPages}
                                                        {Number.isFinite(pagination.totalItems) ? ` · ${pagination.totalItems} receipts` : ''}
                                                    </small>
                                                </nav>
                                            );
                                        })()}
                                    </>
                                ) : (
                                    <div className="text-center p-5">
                                        <p>Nothing here right now.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ReceiptPreviewModal preview={previewImage} onClose={closePreview} />

            <ReceiptDetailsModal receipt={detailsReceipt} onClose={() => setDetailsReceipt(null)} />

            {/* Bulk approve — confirm-then-results modal. Lower z-index than the
                receipt preview above (1055 vs 1060) so a receipt can still be
                inspected full-size from within this modal without it hiding
                behind the preview it opened. */}
            {bulkModalOpen && (
                <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1055 }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content" data-testid="bulk-approve-modal">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {bulkResults
                                        ? 'Bulk approve results'
                                        : `Approve ${selectedReceipts.length} receipt${selectedReceipts.length === 1 ? '' : 's'}?`}
                                </h5>
                                {!bulkInProgress && (
                                    <button type="button" className="close" onClick={closeBulkModal}>
                                        <span>&times;</span>
                                    </button>
                                )}
                            </div>
                            <div className="modal-body">
                                {!bulkResults ? (
                                    <>
                                        <p className="text-muted">
                                            Each receipt below will be approved using its OCR-read amount and the
                                            funding source selected (
                                            <strong>{fundingSourceOptions.find((f) => f.value === bulkFundingSource)?.label || bulkFundingSource}</strong>
                                            ).
                                        </p>
                                        <ul className="list-group mb-0">
                                            {selectedReceipts.map((r) => (
                                                <li key={r._id} className="list-group-item d-flex justify-content-between align-items-center">
                                                    <span>
                                                        {r.userId?.name} {r.userId?.lastName}
                                                        <br />
                                                        <small className="text-muted">{r.fileName}</small>
                                                    </span>
                                                    <span className="text-right">
                                                        {formatDollars(r.grossTotal)}
                                                        {Number.isFinite(r.snapTenderedTotal) && (
                                                            <>
                                                                <br />
                                                                <small className="text-muted">SNAP: {formatDollars(r.snapTenderedTotal)}</small>
                                                            </>
                                                        )}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                        {bulkInProgress && (
                                            <div className="mt-3 text-center">
                                                <span className="spinner-border spinner-border-sm text-primary mr-2" role="status" aria-hidden="true" />
                                                Approving {bulkProgress} of {selectedReceipts.length}…
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <ul className="list-group mb-0">
                                        {bulkResults.map((r) => (
                                            <li
                                                key={r.id}
                                                className={`list-group-item d-flex justify-content-between align-items-center ${
                                                    !r.ok ? 'list-group-item-danger' : r.clamped ? 'list-group-item-warning' : 'list-group-item-success'
                                                }`}
                                            >
                                                <span>
                                                    <i
                                                        className={`fa-solid ${!r.ok ? 'fa-circle-xmark' : r.clamped ? 'fa-triangle-exclamation' : 'fa-circle-check'} mr-2`}
                                                        aria-hidden="true"
                                                    />
                                                    {r.receipt.userId?.name} {r.receipt.userId?.lastName}
                                                    <br />
                                                    <small>{r.message}</small>
                                                </span>
                                                <span>{formatDollars(r.receipt.grossTotal)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="modal-footer">
                                {!bulkResults ? (
                                    <>
                                        <button type="button" className="btn btn-secondary" onClick={closeBulkModal} disabled={bulkInProgress}>
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            style={{ backgroundColor: 'var(--primary)', borderColor: 'var(--primary)' }}
                                            onClick={handleBulkApprove}
                                            disabled={bulkInProgress || selectedReceipts.length === 0}
                                        >
                                            {bulkInProgress
                                                ? 'Approving…'
                                                : `Confirm & Approve ${selectedReceipts.length} Receipt${selectedReceipts.length === 1 ? '' : 's'}`}
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        style={{ backgroundColor: 'var(--primary)', borderColor: 'var(--primary)' }}
                                        onClick={closeBulkModal}
                                    >
                                        Done
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default LoyaltyReceipts;
